// SPDX-License-Identifier: AGPL-3.0-or-later

import * as AccessibilityCommands from '@app/features/accessibility/commands/AccessibilityCommands';
import Accessibility from '@app/features/accessibility/state/Accessibility';
import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {SettingsTabContainer, SettingsTabContent} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {LINK_PREVIEW_EXAMPLE_URL} from '@app/features/app/config/I18nDisplayConstants';
import {Message} from '@app/features/channel/components/Message';
import {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import {Message as MessageModel} from '@app/features/messaging/models/Message';
import {Button} from '@app/features/ui/button/Button';
import {Switch} from '@app/features/ui/components/form/Switch';
import {MockAvatar} from '@app/features/ui/components/MockAvatar';
import {Slider} from '@app/features/ui/components/Slider';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import styles from '@app/features/user/components/modals/tabs/AccessibilityTab.module.css';
import {AnimationTabContent} from '@app/features/user/components/modals/tabs/accessibility_tab/AnimationTab';
import {KeyboardTabContent} from '@app/features/user/components/modals/tabs/accessibility_tab/KeyboardTab';
import {MotionTabContent} from '@app/features/user/components/modals/tabs/accessibility_tab/MotionTab';
import {VisualTabContent} from '@app/features/user/components/modals/tabs/accessibility_tab/VisualTab';
import Users from '@app/features/user/state/Users';
import TtsUtils from '@app/features/voice/utils/TtsUtils';
import {MessagePreviewContext, MessageStates, MessageTypes} from '@app/vendor/constants/ChannelConstants';
import {StatusTypes} from '@app/vendor/constants/StatusConstants';
import {useLingui} from '@lingui/react/macro';
import {PauseIcon, PlayIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';
export const AccessibilityTabPreview = observer(() => {
	const {t} = useLingui();
	const alwaysUnderlineLinks = Accessibility.alwaysUnderlineLinks;
	const fakeData = useMemo(() => {
		const tabOpenedAt = new Date();
		const currentUser = Users.getCurrentUser();
		const author = currentUser?.toJSON() || {
			id: '1000000000000000050',
			username: 'PreviewUser',
			discriminator: '0000',
			global_name: 'Preview User',
			avatar: null,
			avatar_color: null,
			bot: false,
			system: false,
			flags: 0,
		};
		const fakeChannel = new Channel({
			id: '1000000000000000051',
			type: 0,
			name: 'accessibility-preview',
			position: 0,
			parent_id: null,
			topic: null,
			url: null,
			nsfw: false,
			last_message_id: null,
			last_pin_timestamp: null,
			bitrate: null,
			user_limit: null,
			permission_overwrites: [],
		});
		const fakeMessage = new MessageModel(
			{
				id: '1000000000000000052',
				channel_id: '1000000000000000051',
				author,
				type: MessageTypes.DEFAULT,
				flags: 0,
				pinned: false,
				mention_everyone: false,
				content: t`This shows how links appear: ${LINK_PREVIEW_EXAMPLE_URL}`,
				timestamp: tabOpenedAt.toISOString(),
				state: MessageStates.SENT,
			},
			{skipUserCache: true},
		);
		return {fakeChannel, fakeMessage};
	}, []);
	useEffect(() => {
		Channels.handleChannelCreate({channel: fakeData.fakeChannel.toJSON()});
		return () => {
			Channels.handleChannelDelete({channel: fakeData.fakeChannel.toJSON()});
		};
	}, [fakeData.fakeChannel]);
	return (
		<div className={styles.previewWrapper}>
			<div className={styles.previewContainer}>
				<div className={styles.previewActionsRow}>
					<Button small={true} onClick={() => {}}>
						{t`Preview Button`}
					</Button>
					<div className={styles.previewAvatarsRow}>
						<MockAvatar size={32} status={StatusTypes.ONLINE} />
						<MockAvatar size={32} status={StatusTypes.DND} />
						<MockAvatar size={32} status={StatusTypes.IDLE} />
					</div>
				</div>
				<div className={styles.previewMessageContainer}>
					<Message
						channel={fakeData.fakeChannel}
						message={fakeData.fakeMessage}
						previewContext={MessagePreviewContext.SETTINGS}
						previewOverrides={{
							usernameColor: '#e91e63',
							...(alwaysUnderlineLinks
								? {
										linkStyle: 'always-underline',
									}
								: {}),
						}}
					/>
				</div>
			</div>
		</div>
	);
});
export const AccessibilityScreenReaderTabContent: React.FC = observer(() => {
	const {t} = useLingui();
	return (
		<Switch
			label={t`Announce new messages`}
			description={t`Let screen readers announce new messages as they arrive in the open channel. Notification sounds are unaffected.`}
			value={Accessibility.screenReaderAnnounceNewMessages}
			onChange={(value) => AccessibilityCommands.update({screenReaderAnnounceNewMessages: value})}
		/>
	);
});

const TTS_RATE_MARKERS = [0.1, 0.5, 1.0, 1.5, 2.0];

const TTS_RATE_LABEL_ID = 'tts-rate-slider-label';
export const AccessibilityTtsTabContent: React.FC = observer(() => {
	const {t} = useLingui();
	const ttsRate = Accessibility.ttsRate;
	const [isSpeaking, setIsSpeaking] = useState(false);
	const previewMessage = t`Doc, I'm from the future. I came here in a time machine that you invented. Now, I need your help to get back to the year 1985.`;
	const synthesisSupported = TtsUtils.isSupported();
	const [hasVoices, setHasVoices] = useState(() => synthesisSupported && TtsUtils.hasVoices());
	const [playbackError, setPlaybackError] = useState(false);
	useEffect(() => {
		if (!synthesisSupported || typeof window.speechSynthesis === 'undefined') return;
		const synth = window.speechSynthesis;
		const update = () => setHasVoices(TtsUtils.hasVoices());
		update();
		synth.addEventListener('voiceschanged', update);
		let polls = 0;
		const interval = window.setInterval(() => {
			polls += 1;
			update();
			if (TtsUtils.hasVoices() || polls >= 20) {
				window.clearInterval(interval);
			}
		}, 250);
		return () => {
			synth.removeEventListener('voiceschanged', update);
			window.clearInterval(interval);
		};
	}, [synthesisSupported]);
	const previewDisabled = !synthesisSupported || playbackError;
	const handlePreviewToggle = useCallback(() => {
		if (isSpeaking) {
			TtsUtils.stop();
			setIsSpeaking(false);
			return;
		}
		if (previewDisabled) return;
		setPlaybackError(false);
		TtsUtils.speak(previewMessage, {
			rate: ttsRate,
			onEnd: () => setIsSpeaking(false),
			onError: () => {
				setPlaybackError(true);
				setIsSpeaking(false);
			},
		});
		setIsSpeaking(true);
	}, [isSpeaking, previewDisabled, previewMessage, ttsRate]);
	const handleRateChange = useCallback(
		(value: number) => {
			AccessibilityCommands.update({ttsRate: value});
			if (isSpeaking) {
				TtsUtils.stop();
				setIsSpeaking(false);
			}
		},
		[isSpeaking],
	);
	const renderMarkerLabel = useCallback(
		(value: number) => {
			if (value === 0.1) return t`Slower`;
			if (value === 1.0) return `x${value.toFixed(1)}`;
			if (value === 2.0) return t`Faster`;
			return null;
		},
		[t],
	);
	useEffect(() => {
		return () => {
			TtsUtils.stop();
		};
	}, []);
	return (
		<div className={styles.ttsSection}>
			<div className={styles.ttsSliderRow}>
				<div>
					<p id={TTS_RATE_LABEL_ID} className={styles.ttsSliderLabel}>
						{t`Speech playback speed`}
					</p>
					<p className={styles.ttsSliderDescription}>
						{t`Slide toward a relaxed cadence or a brisk delivery for any spoken text.`}
					</p>
				</div>
				<Slider
					className={styles.ttsSlider}
					defaultValue={ttsRate}
					value={ttsRate}
					minValue={0.1}
					maxValue={2.0}
					factoryDefaultValue={1.0}
					markers={TTS_RATE_MARKERS}
					markerPosition="above"
					ariaLabelledBy={TTS_RATE_LABEL_ID}
					onValueChange={handleRateChange}
					asValueChanges={handleRateChange}
					onValueRender={(value) => `x${value.toFixed(1)}`}
					onMarkerRender={renderMarkerLabel}
					showResetButton={true}
					resetTooltip={t`Reset to default speed`}
				/>
			</div>
			<div className={styles.ttsPreviewRow}>
				<Button
					className={styles.ttsPreviewButton}
					leftIcon={isSpeaking ? <PauseIcon size={16} weight="fill" /> : <PlayIcon size={16} weight="fill" />}
					onClick={handlePreviewToggle}
					disabled={!isSpeaking && previewDisabled}
					small={true}
				>
					{isSpeaking ? t`Silence sample` : t`Play sample`}
				</Button>
				<p className={styles.ttsPreviewDescription}>
					{!synthesisSupported
						? t`Speech synthesis is unavailable in your browser.`
						: playbackError
							? t`Speech playback failed. Try again, or check that audio output is working.`
							: hasVoices
								? t`Hear the sample line spoken with your chosen speed.`
								: t`No system voices found; a bundled speech engine will be used.`}
				</p>
			</div>
		</div>
	);
});
export const AccessibilityTab: React.FC = observer(() => {
	const {t} = useLingui();
	return (
		<SettingsTabContainer>
			{!MobileLayout.enabled && <AccessibilityTabPreview />}
			<SettingsTabContent>
				<SettingsSection
					id="visual"
					title={t`Visual`}
					description={t`Customize visual elements to improve visibility and readability.`}
				>
					<VisualTabContent />
				</SettingsSection>
				<SettingsSection
					id="screen-reader"
					title={t`Screen reader`}
					description={t`Control which chat updates are announced by assistive technology.`}
				>
					<AccessibilityScreenReaderTabContent />
				</SettingsSection>
				<SettingsSection
					id="tts"
					title={t`Text-to-speech`}
					description={t`Control how text is spoken and preview the reading rate.`}
				>
					<AccessibilityTtsTabContent />
				</SettingsSection>
				<SettingsSection id="keyboard" title={t`Keyboard`} description={t`Customize keyboard navigation behavior.`}>
					<KeyboardTabContent />
				</SettingsSection>
				<SettingsSection
					id="animation"
					title={t`Animation`}
					description={t`Control animated content throughout the app.`}
				>
					<AnimationTabContent />
				</SettingsSection>
				<SettingsSection
					id="motion"
					title={t`Motion`}
					description={t`Control animations and transitions throughout the app.`}
				>
					<MotionTabContent />
				</SettingsSection>
			</SettingsTabContent>
		</SettingsTabContainer>
	);
});
